import moment from 'moment';

import type { ModelClientComment } from '@app/api/Client';
import type { LeadCardHistoryItemData } from '@app/components/LeadCard/data';

// Строку истории по комментарию собирает бэк: IdS и ActionId —
// CRM.Business/ClientHistory/LogicClientHistory.cs (GetClientCommentsChangesHistory),
// остальные поля — HandlerClientHistoryComment.cs. Достроенная из
// GetLeadCard.ClientComments строка повторяет их один в один: по ObjectType её
// узнаёт LeadMessageItem, по ActionId — фильтр действий, по ObjectId —
// удаление и стопка прилипших комментариев.
export const LEAD_COMMENT_OBJECT_TYPE = 'ClientComment';
export const LEAD_COMMENT_ACTION_ID = 'ChangeClientComment';
const LEAD_COMMENT_ICON = 'fa-solid fa-message';
const LEAD_COMMENT_ITEM_ICON = 'fa-solid fa-circle';
const LEAD_COMMENT_PROPERTY = 'Комментарий';
const LEAD_COMMENT_OBJECT_S = 'комментарий';
const LEAD_COMMENT_ACTION_TEXT = 'добавил комментарий';
const LEAD_HISTORY_TIMESTAMP_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

// В строках истории время уже переведено в зону филиала
// (HandlerClientHistoryComment.ProcessAction → UtcTimeToZone), а
// GetLeadCard.ClientComments отдаёт значение из БД, то есть UTC. Зоны филиала
// в карточке лида нет (см. getPreviousHistoryRange.ts), поэтому переводим в
// зону устройства: у пользователя, работающего в зоне своего филиала, время
// совпадает с тем, что показывают строки истории.
const toLeadHistoryTimestamp = (timestamp: string): string => {
  const utcMoment = moment.utc(timestamp);

  return utcMoment.isValid()
    ? utcMoment.local().format(LEAD_HISTORY_TIMESTAMP_FORMAT)
    : timestamp;
};

const getTimestampValue = (timestamp: string): number => {
  const parsed = Date.parse(timestamp);

  return Number.isNaN(parsed) ? 0 : parsed;
};

export const getLeadCommentHistoryKey = (commentId: number): string =>
  `${LEAD_COMMENT_OBJECT_TYPE}:${commentId}`;

export const buildLeadCommentHistoryItem = (
  comment: ModelClientComment
): LeadCardHistoryItemData => ({
  Timestamp: toLeadHistoryTimestamp(comment.Timestamp),
  ActionId: LEAD_COMMENT_ACTION_ID,
  ActionUserId: comment.UserId,
  ObjectId: comment.Id,
  Icon: LEAD_COMMENT_ICON,
  ActionUser: comment.UserName,
  ObjectType: LEAD_COMMENT_OBJECT_TYPE,
  ObjectS: LEAD_COMMENT_OBJECT_S,
  ActionText: LEAD_COMMENT_ACTION_TEXT,
  IsImportantAction: false,
  ActionItems: [
    {
      Icon: LEAD_COMMENT_ITEM_ICON,
      Property: LEAD_COMMENT_PROPERTY,
      ValueOld: null,
      ValueNew: comment.Comment,
    },
  ],
  Files: null,
  ChatMessages: null,
  IdS: `comment-change-${comment.Id}`,
});

interface MergeLeadCommentHistoryItemsParams {
  items: LeadCardHistoryItemData[];
  comments: ModelClientComment[] | null | undefined;
  // Ключи строк, убранных из истории вручную (удаление комментария) — до
  // ответа обновлённой карточки такой комментарий ещё придёт в ClientComments,
  // а показывать его уже нельзя.
  removedKeys?: ReadonlySet<string>;
}

// Комментарии из GetLeadCard встраиваются в уже отсортированный по времени
// список истории: те, что пришли и строкой истории, пропускаются (иначе
// комментарий задвоится), остальные встают на своё место по времени. Порядок
// самих строк истории не меняется — их сортировку задаёт
// mergeLeadHistoryByStableKey, и пересортировывать её здесь нельзя.
export const mergeLeadCommentHistoryItems = ({
  items,
  comments,
  removedKeys,
}: MergeLeadCommentHistoryItemsParams): LeadCardHistoryItemData[] => {
  if (!comments?.length) return items;

  const knownCommentIds = new Set(
    items
      .filter(({ ObjectType }) => ObjectType === LEAD_COMMENT_OBJECT_TYPE)
      .map(({ ObjectId }) => ObjectId)
  );

  const missingComments = comments
    .filter(({ Id }) => !knownCommentIds.has(Id))
    .filter(({ Id }) => !removedKeys?.has(getLeadCommentHistoryKey(Id)))
    .map(buildLeadCommentHistoryItem)
    .sort(
      (a, b) => getTimestampValue(a.Timestamp) - getTimestampValue(b.Timestamp)
    );

  if (!missingComments.length) return items;

  const merged: LeadCardHistoryItemData[] = [];
  let commentIndex = 0;

  for (const item of items) {
    const itemValue = getTimestampValue(item.Timestamp);

    while (
      commentIndex < missingComments.length &&
      getTimestampValue(missingComments[commentIndex].Timestamp) <= itemValue
    ) {
      merged.push(missingComments[commentIndex]);
      commentIndex += 1;
    }

    merged.push(item);
  }

  return merged.concat(missingComments.slice(commentIndex));
};

interface GetPinnedCommentsAheadOfHistoryParams {
  items: LeadCardHistoryItemData[];
  comments: ModelClientComment[] | null | undefined;
  isHistoryFullyLoaded: boolean;
  removedKeys?: ReadonlySet<string>;
}

// Закреплённые комментарии, до даты которых история ещё не догружена: они
// старше самой старой загруженной строки. mergeLeadCommentHistoryItems ставит
// их в начало списка, и на верху ленты sticky-стопка разворачивалась в начале
// каждой подгруженной порции (issue/11369). Такие комментарии лента держит
// стопкой сверху, пока история не дойдёт до их места.
export const getPinnedCommentsAheadOfHistory = ({
  items,
  comments,
  isHistoryFullyLoaded,
  removedKeys,
}: GetPinnedCommentsAheadOfHistoryParams): Set<number> => {
  if (isHistoryFullyLoaded || !comments?.length) return new Set();

  const knownCommentIds = new Set(
    items
      .filter(({ ObjectType }) => ObjectType === LEAD_COMMENT_OBJECT_TYPE)
      .map(({ ObjectId }) => ObjectId)
  );

  const oldestLoadedValue = items.reduce(
    (oldest, { Timestamp }) => Math.min(oldest, getTimestampValue(Timestamp)),
    Number.POSITIVE_INFINITY
  );

  return new Set(
    comments
      .filter(({ IsUnpinned }) => !IsUnpinned)
      .filter(({ Id }) => !knownCommentIds.has(Id))
      .filter(({ Id }) => !removedKeys?.has(getLeadCommentHistoryKey(Id)))
      .filter(
        ({ Timestamp }) =>
          getTimestampValue(toLeadHistoryTimestamp(Timestamp)) <
          oldestLoadedValue
      )
      .map(({ Id }) => Id)
  );
};
